/* ============================================================
   AssistantFAB — floating bottom-right button that opens the existing
   AI assistant chat panel (owned by MainApp). Visual port of pack's
   Assistant.html FAB. Clicking dispatches a CustomEvent so the panel
   can react from wherever it's mounted.
   ============================================================ */
import React, { useEffect, useRef, useState } from 'react';
import { Icons as I } from '../pack-icons';
import { useDashboardData } from '../../state/DashboardDataContext.jsx';
import { deriveAssistantAllowance } from '../../lib/assistantAllowance.js';
import { effectiveAccessState } from '../../lib/billingState.js';

const OPEN_EVENT = 'dathent:assistant:open';
const CLOSE_EVENT = 'dathent:assistant:close';
const STATE_EVENT = 'dathent:assistant:state';

export default function AssistantFAB() {
  const [open, setOpen] = useState(false);
  const [hasNew, setHasNew] = useState(true);
  // TASK-314 follow-up: the panel itself (AIAssistant.jsx's AIPanel) already
  // shows a locked "AI Assistant is available from Starter." state instead of
  // a working chat once assistant_turns_monthly is confirmed 0 (Scheduler /
  // posting plan) — see lib/assistantAllowance.js. That protects every
  // entry point (this FAB, Cmd+K, a notification deep link) from ever
  // reaching a doomed request. What it does NOT do is stop the FAB itself
  // from inviting a click it can't honour. Reuse the SAME tested predicate
  // here, fail-open (render the FAB) until the allowance is positively known
  // to be zero — never hide it while entitlements are merely still loading.
  const dash = useDashboardData() || {};
  const allowance = deriveAssistantAllowance(dash.entitlements);
  const locked = effectiveAccessState(dash.entitlements) !== 'lapsed' && allowance.known && allowance.zero;
  const entitlementsFetchStartedRef = useRef(false);

  useEffect(() => {
    if (dash.entitlements != null || entitlementsFetchStartedRef.current) return;
    entitlementsFetchStartedRef.current = true;
    Promise.resolve(dash.ensureDashboardData?.({ keys: ['entitlements'] }) || null).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dash.entitlements]);

  // Allow other code to broadcast open/close state (existing MainApp will fire
  // CLOSE_EVENT when the panel is closed via internal UI).
  useEffect(() => {
    const onState = (e) => {
      if (typeof e.detail?.open === 'boolean') setOpen(e.detail.open);
    };
    const onClose = () => setOpen(false);
    const onOpen = () => { setOpen(true); setHasNew(false); };
    window.addEventListener(STATE_EVENT, onState);
    window.addEventListener(CLOSE_EVENT, onClose);
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => {
      window.removeEventListener(STATE_EVENT, onState);
      window.removeEventListener(CLOSE_EVENT, onClose);
      window.removeEventListener(OPEN_EVENT, onOpen);
    };
  }, []);

  // Cmd+K / Ctrl+K toggle — a no-op while locked, same as the FAB not
  // rendering: no keyboard shortcut should open a panel this plan can't use.
  useEffect(() => {
    const onKey = (e) => {
      if (locked) return;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        toggle();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, locked]);

  const toggle = () => {
    if (open) {
      window.dispatchEvent(new CustomEvent(CLOSE_EVENT));
    } else {
      window.dispatchEvent(new CustomEvent(OPEN_EVENT));
    }
  };

  // A recommendation notification can still deep-link into the panel via
  // OPEN_EVENT (NotificationBell) while this FAB is hidden — AIPanel itself
  // renders the locked copy for that case, so nothing further is needed here.
  if (locked) return null;

  return (
    <button
      className={`as-fab ${open ? 'is-open' : ''} ${hasNew && !open ? 'has-new' : ''}`}
      onClick={toggle}
      aria-label={open ? 'Close assistant' : 'Open assistant'}
      type="button"
    >
      <span className="as-fab-ring" />
      <span className="as-fab-ring as-fab-ring-2" />
      <span className="as-fab-ic">
        {open ? (
          <I.x size={20} />
        ) : (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <defs>
              <linearGradient id="fabSparkleGrad" x1="0" x2="1" y1="0" y2="1">
                <stop offset="0%" stopColor="#fff" stopOpacity="1" />
                <stop offset="100%" stopColor="#fff" stopOpacity="0.85" />
              </linearGradient>
            </defs>
            <path
              d="M12 3 13.5 8 18 9.5 13.5 11 12 16 10.5 11 6 9.5 10.5 8z"
              fill="url(#fabSparkleGrad)"
            />
            <path
              d="M19 14l.8 2.2L22 17l-2.2.8L19 20l-.8-2.2L16 17l2.2-.8z"
              fill="#fff"
              opacity="0.9"
            />
          </svg>
        )}
      </span>
      {hasNew && !open && <span className="as-fab-badge" />}
    </button>
  );
}
